import React from "react";
import { useTable, Column, useSortBy } from "react-table";
import {
  BsPlusCircle,
  BsFilter,
  BsSearch,
  BsTrash,
  BsArrowClockwise,
  BsSortDown,
  BsSortUp,
  BsPencil,
  BsClipboardPlus,
} from "react-icons/bs";
import { Oval } from "react-loader-spinner";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { Order, OrderStatus } from "../../../models";
import {
  getOrders,
  refreshOrders,
  setShowUpdateOrderModal,
  setShowDeleteOrderModal,
  setShowPickupInstructionsModal,
} from "../ordersSlice";
import { FiltersModal } from "../FiltersModal/FiltersModal";
import { OrderFilters } from "../../../datasource";

import "./Orders.scss";
import { PhoneNumber } from "../../../components";

const PAGE_SIZE = 10;

type ColumnsType = Order;

// Read the persisted search term + filters out of the URL query string so they
// survive navigating into an order's details and back.
function parseSearchFromParams(params: URLSearchParams): string {
  return params.get("search") || "";
}

function parseFiltersFromParams(params: URLSearchParams): OrderFilters {
  const statusesParam = params.get("statuses");

  return {
    // `null` (param absent) falls back to the default view; an explicit empty
    // string (user deselected every status) is preserved as an empty list.
    statuses:
      statusesParam === null
        ? [OrderStatus.OrderStatusCreated]
        : (statusesParam.split(",").filter(Boolean) as OrderStatus[]),
    createdStartDate: params.get("createdStart") || undefined,
    createdEndDate: params.get("createdEnd") || undefined,
    pickupStartDate: params.get("pickupStart") || undefined,
    pickupEndDate: params.get("pickupEnd") || undefined,
  };
}

function buildSearchParams(
  search: string,
  filters: OrderFilters
): URLSearchParams {
  const params = new URLSearchParams();

  if (search) params.set("search", search);
  params.set("statuses", filters.statuses.join(","));
  if (filters.createdStartDate) params.set("createdStart", filters.createdStartDate);
  if (filters.createdEndDate) params.set("createdEnd", filters.createdEndDate);
  if (filters.pickupStartDate) params.set("pickupStart", filters.pickupStartDate);
  if (filters.pickupEndDate) params.set("pickupEnd", filters.pickupEndDate);

  return params;
}

export function Orders() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const orders = useAppSelector((state) => state.orders.orders) || [];
  const ordersAPI = useAppSelector((state) => state.orders.getOrders);
  const cancelOrderAPI = useAppSelector(
    (state) => state.cancelOrder.cancelOrder
  );
  const orderToCancel = useAppSelector(
    (state) => state.orders.showDeleteOrderModal
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const [fetchOrders, setFetchOrders] = React.useState<boolean>(true);
  const [offset, setOffset] = React.useState<number>(0);
  const [search, setSearch] = React.useState<string>(() =>
    parseSearchFromParams(searchParams)
  );
  const [showFiltersModal, setShowFiltersModal] = React.useState(false);
  const [filters, setFilters] = React.useState<OrderFilters>(() =>
    parseFiltersFromParams(searchParams)
  );

  // Mirror the current search + filters into the URL so a round-trip to an
  // order's details (and `navigate(-1)` back) restores them. `replace` keeps
  // typing in the search box from flooding the history stack.
  React.useEffect(() => {
    setSearchParams(buildSearchParams(search, filters), { replace: true });
  }, [search, filters]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setSearch(e.target.value);
  };

  const data = React.useMemo(() => {
    if (search === "") return orders;

    const searchBy = search.toLowerCase();
    return orders.filter(
      (o) =>
        o.name.toLowerCase().includes(searchBy) ||
        o.phoneNumber.toLowerCase().includes(searchBy) ||
        o.address.toLowerCase().includes(searchBy) ||
        o.zip.toLowerCase().includes(searchBy) ||
        o.status.toLocaleLowerCase().includes(searchBy)
    );
  }, [orders, search]);

  React.useEffect(() => {
    if (
      !ordersAPI.loading &&
      !!ordersAPI.value &&
      ordersAPI.value.length === PAGE_SIZE
    ) {
      setOffset(offset + PAGE_SIZE);
    }
  }, [ordersAPI.loading, !!ordersAPI.value]);

  React.useEffect(() => {
    // We already fetched orders
    if (fetchOrders || offset !== 0) {
      dispatch(
        getOrders({
          pagination: {
            offset,
            count: PAGE_SIZE,
          },
          filters,
        })
      );

      setFetchOrders(false);
    }
  }, [offset, fetchOrders]);

  const onCloseFilters = () => {
    setOffset(0);
    setFetchOrders(true);

    setShowFiltersModal(false);
  };

  const onAddOrderClick = () => {
    navigate("/create-order");
  };

  const onRefreshClick = () => {
    dispatch(refreshOrders());
    setOffset(0);
    setFetchOrders(true);
  };

  const columns: Array<Column<ColumnsType>> = React.useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
      },
      {
        Header: "Address",
        accessor: "address",
      },
      {
        Header: "ZIP",
        accessor: "zip",
      },
      {
        Header: "Phone Number",
        accessor: "phoneNumber",
        Cell: (props) => (
          <div style={{ width: 120 }}>
            <PhoneNumber phoneNumber={props.row.original.phoneNumber} />
          </div>
        ),
      },
      {
        Header: "Service Type",
        accessor: "service",
      },
      {
        Header: "Dumpster Size",
        accessor: "dumpsterSize",
      },
      {
        Header: "Material Disposing",
        accessor: "materialDisposing",
      },
      {
        Header: "Material Description",
        accessor: "description",
      },
      {
        id: "price",
        Header: "Base Price",
        accessor: (row) => `$${row.price}.00`,
      },
      {
        id: "discount",
        Header: "Discount",
        accessor: (row) => `$${row.discount}.00`,
      },
      {
        id: "delivery_fee",
        Header: "Delivery Fee",
        accessor: (row) => `$${row.deliveryFee}.00`,
      },
      {
        id: "additionalCharges",
        Header: "Additional Charges",
        accessor: (row) => `$${row.additionalCharges}.00`,
      },
      {
        id: "totalPrice",
        Header: "Total Price",
        accessor: (row) =>
          `$${
            row.price + row.deliveryFee + row.additionalCharges - row.discount
          }.00`,
      },
      {
        Header: "Created Date",
        accessor: "createdAt",
      },
      {
        Header: "Delivery Date",
        accessor: "deliveryDate",
      },
      {
        Header: "Pickup Date",
        accessor: "pickupDate",
      },
      {
        Header: "Instructions",
        accessor: "specialInstructions",
      },
      {
        Header: "Actions",
        accessor: "id",
        Cell: (props) => {
          const { value, row } = props;

          const onCancelOrder = (e: React.MouseEvent) => {
            e.stopPropagation();
            dispatch(setShowDeleteOrderModal(value));
          };

          const onEditOrder = (e: React.MouseEvent) => {
            e.stopPropagation();
            dispatch(setShowUpdateOrderModal(row.original));
          };

          const onAddPickupInstructions = (e: React.MouseEvent) => {
            e.stopPropagation();
            dispatch(setShowPickupInstructionsModal(value));
          };

          return (
            <div className="actions-cell" onClick={(e) => e.stopPropagation()}>
              <div className="update-icon-container" onClick={onEditOrder}>
                <BsPencil className="update-icon" />
              </div>
              {row.original.status === OrderStatus.OrderStatusCreated ? (
                cancelOrderAPI.loading && orderToCancel === value ? (
                  <Oval
                    color="#131313"
                    height={20}
                    width={20}
                    secondaryColor="#FBD20A"
                  />
                ) : (
                  <BsTrash className="delete-icon" onClick={onCancelOrder} />
                )
              ) : null}
              <div
                className="instructions-icon-container"
                onClick={onAddPickupInstructions}
              >
                <BsClipboardPlus className="instructions-icon" />
              </div>
            </div>
          );
        },
      },
      {
        Header: "Status",
        accessor: "status",
      },
      {
        Header: "Email",
        accessor: "email",
      },
      {
        id: "id1",
        Header: "ID",
        accessor: (row) => row.id,
      },
      {
        Header: "PO",
        accessor: "po",
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
        initialState: { sortBy: [{ id: "name", desc: false }] },
      },
      useSortBy
    );

  return (
    <div className="orders-component">
      <div className="controllers-container">
        <div className="left-controllers-container">
          <div className="title-container">
            <div className="title-text">Orders</div>
            <div className="title-count">{data.length}</div>
          </div>

          <div className="search-container">
            <BsSearch className="search-icon" />
            <input
              value={search}
              placeholder="Search"
              onChange={onInputChange}
            />
          </div>

          <div className="refresh-button" onClick={onRefreshClick}>
            <BsArrowClockwise />
          </div>

          <div
            className="filter-button"
            onClick={() => setShowFiltersModal(true)}
          >
            <BsFilter className="icon" />
            Filter
          </div>
        </div>

        <div className="add-button" onClick={onAddOrderClick}>
          <BsPlusCircle className="icon" />
          Add Order
        </div>
      </div>

      {offset === 0 && ordersAPI.loading && (
        <Oval color="#131313" height={20} width={20} secondaryColor="#FBD20A" />
      )}

      <div className="table-container">
        <table {...getTableProps()} className="tp-table">
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    <div className="header-cell">
                      {column.render("Header")}
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <BsSortDown className="sorting-icon" />
                        ) : (
                          <BsSortUp className="sorting-icon" />
                        )
                      ) : null}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  onClick={() => navigate(`/orders/${row.original.id}`)}
                  className="clickable-row"
                >
                  {row.cells.map((cell) => {
                    return (
                      <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <FiltersModal
        isOpen={showFiltersModal}
        onClose={onCloseFilters}
        filters={filters}
        setFilters={setFilters}
      />
    </div>
  );
}
